import React, { useState } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  Email,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import api from '../../services/api';

interface EmailVerificationPromptProps {
  onClose?: () => void;
}

const EmailVerificationPrompt: React.FC<EmailVerificationPromptProps> = ({ onClose }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleResendVerification = async () => {
    if (!user?.email) return;

    try {
      setSending(true);
      setError(false);
      
      const response = await api.post('/resend-verification-email-by-email', null, {
        params: { email: user.email }
      });
      
      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      console.error('Failed to resend verification email:', err);
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(false);
  };

  return (
    <>
      <Alert 
        severity="warning" 
        sx={{ 
          mb: 3,
          '& .MuiAlert-icon': {
            fontSize: 28
          }
        }}
        action={
          onClose && (
            <Button color="inherit" size="small" onClick={onClose}>
              Dismiss
            </Button>
          )
        }
      >
        <AlertTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
          Email Verification Required
        </AlertTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Your email address <strong>{user?.email}</strong> has not been verified yet. 
          You need to verify your email before you can access the platform features.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Email />}
            onClick={handleResendVerification}
            disabled={sending}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': {
                backgroundColor: '#f57c00',
              },
            }}
          >
            {sending ? 'Sending...' : 'Resend Verification Email'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Check your spam folder if you don't see the email
          </Typography>
        </Box>
      </Alert>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Verification email sent! Please check your inbox.
        </Alert>
      </Snackbar>

      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          Failed to send verification email. Please try again later.
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmailVerificationPrompt;